//! Tracks the startup time of the app

import { debugFlag, debugFlags } from '@/app/debugFlags/debugFlags';
import { events } from '@/app/events/events';

// Timer configuration with levels (0 = top level, higher numbers = nested)
const timerInfo = {
  firstRender: { startTime: 0, endTime: 0 },
  // Preload timers (run in _root loader, before file load)
  preload: { startTime: 0, endTime: 0 },
  'preload.wasm': { startTime: 0, endTime: 0 },
  'preload.workerWasm': { startTime: 0, endTime: 0 },
  'preload.assets': { startTime: 0, endTime: 0 },
  'preload.coreWorkers': { startTime: 0, endTime: 0 },
  'preload.langWorkers': { startTime: 0, endTime: 0 },
  'preload.emptyGrid': { startTime: 0, endTime: 0 },
  // Core worker timers (run inside core.worker)
  'core.loadFile': { startTime: 0, endTime: 0 },
  'core.loadFile.fetchGridFile': { startTime: 0, endTime: 0 },
  'core.loadFile.loadCore': { startTime: 0, endTime: 0 },
  'core.loadFile.newFromFile': { startTime: 0, endTime: 0 },
  // File loader timers (run when navigating to file)
  'file.loader': { startTime: 0, endTime: 0 },
  'file.loader.files.get': { startTime: 0, endTime: 0 },
  'file.loader.embeds.get': { startTime: 0, endTime: 0 },
  'file.loader.waitForPreload': { startTime: 0, endTime: 0 },
  'file.loader.initCoreClient': { startTime: 0, endTime: 0 },
  'file.loader.loadPixi': { startTime: 0, endTime: 0 },
  'file.loader.quadraticCore.load': { startTime: 0, endTime: 0 },
  // App initialization timers
  pixiApp: { startTime: 0, endTime: 0 },
  multiplayerSync: { startTime: 0, endTime: 0 },
  offlineSync: { startTime: 0, endTime: 0 },
};

export type TimerNames = keyof typeof timerInfo | 'fileSize';

class StartupTimer {
  private sent = false;
  private fileSize: number = 0;

  constructor() {
    events.on('startupTimer', this.handleStartupTimer);
  }

  reset(): void {
    this.sent = false;
    this.fileSize = 0;
    for (const key of Object.keys(timerInfo) as (keyof typeof timerInfo)[]) {
      // Preserve preload timers - they only run once and we want to see them on first file load
      if (key.startsWith('preload')) continue;
      timerInfo[key].startTime = 0;
      timerInfo[key].endTime = 0;
    }
  }

  resetTimer(name: TimerNames): void {
    if (name === 'fileSize') return;
    timerInfo[name].startTime = 0;
    timerInfo[name].endTime = 0;
  }

  private handleStartupTimer = (name: TimerNames, data: { start?: number; end?: number }) => {
    if (name === 'fileSize') {
      this.fileSize = data.start ?? 0;
      return;
    }

    if (data.start) {
      this.start(name, data.start);
    }

    if (data.end) {
      this.end(name, data.end);
    }
  };

  start(name: TimerNames, startTime = performance.now()): void {
    if (name === 'fileSize') throw new Error('fileSize is not a valid timer name');
    if (timerInfo[name].startTime && debugFlags.debugAvailable) {
      console.error(`startupTimer.start called twice for ${name}`);
      return;
    }
    timerInfo[name].startTime = startTime;
  }

  end(name: TimerNames, endTime = performance.now()): void {
    if (name === 'fileSize') throw new Error('fileSize is not a valid timer name');
    const startTime = timerInfo[name].startTime;
    if (!startTime) {
      if (debugFlags.debugAvailable) {
        console.error(`Expected startupTimer.start('${name}') to be called before startupTimer.end('${name}')`);
      }
      return;
    }
    timerInfo[name].endTime = endTime;
  }

  show(): Record<string, number> | undefined {
    if (this.sent) return undefined;

    // Only show timers if we actually loaded a file (file.loader was started and completed)
    const fileLoaderTimer = timerInfo['file.loader'];
    if (!fileLoaderTimer.startTime || !fileLoaderTimer.endTime) {
      // Not a file load - don't show timers or mark as sent
      return undefined;
    }

    const result: Record<string, number> = {
      fileSize: this.fileSize,
    };

    // Calculate durations for all completed timers
    const durations: Record<string, number> = {};
    for (const [name, { endTime, startTime }] of Object.entries(timerInfo)) {
      if (startTime > 0 && endTime >= startTime) {
        durations[name] = endTime - startTime;
        result[name] = durations[name];
      }
    }

    if (debugFlag('debugStartupTime')) {
      const fileLoadStart = fileLoaderTimer.startTime;
      const preloadEnd = timerInfo['preload'].endTime;
      const preloadWasComplete = preloadEnd > 0 && preloadEnd < fileLoadStart;

      const lines: string[] = [];
      lines.push(`File Load Summary (${(this.fileSize / (1024 * 1024)).toFixed(2)} MB)`);
      lines.push('='.repeat(50));

      // Show preload status
      if (preloadWasComplete) {
        const preloadFinishedBefore = Math.round(fileLoadStart - preloadEnd);
        lines.push(`✓ Preload finished ${preloadFinishedBefore}ms before file click`);
      } else if (preloadEnd > 0) {
        const waitTime = durations['file.loader.waitForPreload'] || 0;
        lines.push(`⚠ Preload was still running - waited ${Math.round(waitTime)}ms`);
      } else {
        lines.push(`⚠ Preload did not complete`);
      }

      // Show file load time
      lines.push(`\nFile Load: ${Math.round(durations['file.loader'] || 0)}ms`);

      // Show breakdown of file load
      const fileLoadTimers = [
        ['  API call', 'file.loader.files.get'],
        ['  Wait for preload', 'file.loader.waitForPreload'],
        ['  Core load', 'file.loader.quadraticCore.load'],
      ] as const;

      for (const [label, key] of fileLoadTimers) {
        if (durations[key] !== undefined) {
          lines.push(`${label}: ${Math.round(durations[key])}ms`);
        }
      }

      // Show if fallback initialization happened (these shouldn't run if preload completed)
      const fallbackTimers = ['file.loader.initCoreClient', 'file.loader.loadPixi'] as const;
      const fallbacksRan = fallbackTimers.filter((t) => durations[t] !== undefined);
      if (fallbacksRan.length > 0) {
        lines.push(`\n⚠ Fallback init ran (preload incomplete):`);
        for (const key of fallbacksRan) {
          lines.push(`  ${key}: ${Math.round(durations[key])}ms`);
        }
      }

      // Show core worker breakdown
      if (durations['core.loadFile'] !== undefined) {
        lines.push(`\nCore Worker Breakdown:`);
        const coreTimers = [
          ['  Fetch file', 'core.loadFile.fetchGridFile'],
          ['  Load WASM', 'core.loadFile.loadCore'],
          ['  Parse file', 'core.loadFile.newFromFile'],
        ] as const;
        for (const [label, key] of coreTimers) {
          if (durations[key] !== undefined && durations[key] > 0) {
            lines.push(`${label}: ${Math.round(durations[key])}ms`);
          }
        }
      }

      // Show preload breakdown (for reference)
      if (durations['preload'] !== undefined) {
        lines.push(`\nPreload Breakdown (ran during dashboard):`);
        const preloadTimers = [
          ['  Main WASM', 'preload.wasm'],
          ['  Worker WASM', 'preload.workerWasm'],
          ['  Assets', 'preload.assets'],
          ['  Empty grid', 'preload.emptyGrid'],
        ] as const;
        for (const [label, key] of preloadTimers) {
          if (durations[key] !== undefined) {
            lines.push(`${label}: ${Math.round(durations[key])}ms`);
          }
        }
      }

      console.log(lines.join('\n'));
    }

    this.sent = true;
    return result;
  }
}

export const startupTimer = new StartupTimer();
